import { NATURAL_RANKS, createDeck, createSeededRandom, enumerateLegalPlays, getCard, isNaturalRank, legalReturnTributeCards, legalTributeCards, rankStrength, shuffleCards, } from "@guandan/game-core";
const defaultBudgets = {
    standard: 8,
    expert: 35,
    master: 120,
};
function teamOf(seat) {
    return (seat % 2);
}
function teammateOf(seat) {
    return ((seat + 2) % 4);
}
function removeCards(hand, cardIds) {
    const removed = new Set(cardIds);
    return hand.filter((cardId) => !removed.has(cardId));
}
function rankFragmentation(hand, observation) {
    const counts = new Map();
    let jokers = 0;
    for (const cardId of hand) {
        const card = getCard(cardId);
        if (!isNaturalRank(card.rank))
            jokers += 1;
        else
            counts.set(card.rank, (counts.get(card.rank) ?? 0) + 1);
    }
    let score = counts.size * 7 + jokers * 3;
    for (const count of counts.values()) {
        if (count === 1)
            score += 8;
        else if (count === 2)
            score += 2;
        else if (count >= 4)
            score -= count * 2;
    }
    const wildcards = hand.filter((cardId) => {
        const card = getCard(cardId);
        return card.suit === "hearts" && card.rank === observation.levelRank;
    }).length;
    return score - wildcards * 5;
}
function teammateOwnsIncumbent(observation) {
    return (observation.lastPlay !== null &&
        teamOf(observation.lastPlay.seat) === observation.team);
}
function opponentThreat(observation) {
    return Math.min(...observation.players
        .filter((player) => player.team !== observation.team && player.finishOrder === null)
        .map((player) => player.remainingCards), 27);
}
function threatDefenseScore(play, observation, difficulty) {
    if (difficulty === "standard")
        return 0;
    const threat = opponentThreat(observation);
    if (threat > 5)
        return 0;
    const incumbentThreat = observation.lastPlay
        ? observation.players.find((player) => player.seat === observation.lastPlay?.seat)
        : null;
    const defendingAgainstThreat = incumbentThreat?.team !== observation.team &&
        (incumbentThreat?.remainingCards ?? 27) <= 5;
    let score = (6 - threat) * 35;
    if (observation.lastPlay === null) {
        // A player on one card can only leave on a single. Lead a pair or a wider
        // structure whenever possible, and make the lead expensive to overtake.
        if (threat <= 1)
            score += play.cardIds.length === 1 ? -900 : 520;
        else
            score += Math.min(play.cardIds.length, 5) * 45;
        score += play.combination.mainValue * (threat <= 2 ? 7 : 3);
    }
    else if (defendingAgainstThreat) {
        // Do not "save" a bomb while the opponent who owns the trick is about to
        // finish. A higher control play is worth more than hand neatness here.
        score += play.combination.mainValue * 8;
        if (play.combination.isBomb)
            score += threat <= 2 ? 620 : 260;
    }
    return score;
}
function structureBreakCost(play, observation) {
    if (play.cardIds.length === observation.ownHand.length)
        return 0;
    const held = groupedCount(observation.ownHand);
    const used = groupedCount(play.cardIds);
    let cost = 0;
    for (const [rank, usedCount] of used) {
        const heldCount = held.get(rank) ?? 0;
        if (heldCount >= 4 && usedCount < heldCount)
            cost += 360 + (heldCount - usedCount) * 45;
        else if (heldCount === 3 && usedCount < 3)
            cost += 70;
        else if (heldCount === 2 && usedCount === 1)
            cost += 24;
    }
    const wildcardCount = play.cardIds.filter((cardId) => {
        const card = getCard(cardId);
        return card.rank === observation.levelRank && card.suit === "hearts";
    }).length;
    if (wildcardCount > 0 &&
        !play.combination.isBomb &&
        play.combination.kind !== "straight-flush")
        cost += wildcardCount * 115;
    return cost;
}
function nextUnfinishedPlayer(observation, after) {
    let seat = ((after + 3) % 4);
    for (let count = 0; count < 4; count += 1) {
        const player = observation.players.find((candidate) => candidate.seat === seat);
        if (player?.finishOrder === null)
            return player;
        seat = ((seat + 3) % 4);
    }
    return null;
}
function partnershipScore(play, observation, difficulty) {
    if (difficulty === "standard" || observation.lastPlay !== null)
        return 0;
    const teammate = observation.players.find((player) => player.seat === teammateOf(observation.seat));
    const nextPlayer = nextUnfinishedPlayer(observation, observation.seat);
    let score = 0;
    // Do not lead the exact shape that lets the next opponent walk out. When the
    // partner is the shortest hand, feed a low card group of the size they are
    // most likely to hold instead of mechanically dumping the largest combo.
    if (nextPlayer &&
        nextPlayer.team !== observation.team &&
        nextPlayer.remainingCards <= 2) {
        if (play.cardIds.length === nextPlayer.remainingCards)
            score -= 780;
        if (nextPlayer.remainingCards === 1 && play.cardIds.length > 1)
            score += 260;
    }
    if (teammate?.finishOrder === null &&
        teammate.remainingCards <= 3 &&
        opponentThreat(observation) > teammate.remainingCards) {
        if (play.cardIds.length === teammate.remainingCards)
            score += 190 - play.combination.mainValue * 4;
        else if (teammate.remainingCards === 1 && play.cardIds.length === 1)
            score += 150 - play.combination.mainValue * 4;
    }
    return score;
}
function basePlayScore(play, observation, difficulty) {
    const remaining = removeCards(observation.ownHand, play.cardIds);
    let score = play.cardIds.length * 30 - play.combination.mainValue;
    if (remaining.length === 0)
        score += 10_000;
    if (play.combination.isBomb && remaining.length > 0)
        score -= 120 + play.cardIds.length * 8;
    if (difficulty !== "standard") {
        score -= structureBreakCost(play, observation);
        score += partnershipScore(play, observation, difficulty);
        score -= rankFragmentation(remaining, observation) * 3;
        const weakestOpponent = Math.min(...observation.players
            .filter((player) => player.team !== observation.team && player.finishOrder === null)
            .map((player) => player.remainingCards));
        if (weakestOpponent <= 3 && play.combination.isBomb)
            score += 100;
        const teammate = observation.players.find((player) => player.seat === teammateOf(observation.seat));
        if (teammate &&
            teammate.remainingCards <= 3 &&
            observation.lastPlay === null) {
            score -= play.combination.mainValue * 2;
        }
        if (observation.lastPlay === null &&
            opponentThreat(observation) > 5 &&
            remaining.length > 5) {
            score -= Math.max(0, play.combination.mainValue - 8) * 5;
        }
    }
    return score + threatDefenseScore(play, observation, difficulty);
}
function tributeAction(observation) {
    const tribute = observation.tribute;
    if (!tribute)
        return null;
    const owes = tribute.donors.includes(observation.seat) &&
        !tribute.offers.some((offer) => offer.from === observation.seat);
    if (owes) {
        const cardId = legalTributeCards(observation.ownHand, observation.levelRank)[0];
        return cardId ? { type: "TRIBUTE", seat: observation.seat, cardId } : null;
    }
    const transfer = tribute.transfers.find((item) => item.to === observation.seat && item.returnCardId === null);
    if (!transfer)
        return null;
    const cardId = legalReturnTributeCards(observation.ownHand, observation.levelRank)[0];
    return cardId
        ? {
            type: "RETURN_TRIBUTE",
            seat: observation.seat,
            to: transfer.from,
            cardId,
        }
        : null;
}
function sampleUnknownHands(observation, seed) {
    const known = new Set([...observation.ownHand, ...observation.playedCards]);
    const unknown = createDeck()
        .map((card) => card.id)
        .filter((cardId) => !known.has(cardId));
    const shuffled = shuffleCards(unknown, seed);
    const hands = { 0: [], 1: [], 2: [], 3: [] };
    let offset = 0;
    for (const player of observation.players) {
        if (player.seat === observation.seat) {
            hands[player.seat] = observation.ownHand;
            continue;
        }
        hands[player.seat] = Object.freeze(shuffled.slice(offset, offset + player.remainingCards));
        offset += player.remainingCards;
    }
    return Object.freeze({ hands: Object.freeze(hands) });
}
function groupedCount(hand) {
    const counts = new Map();
    for (const cardId of hand) {
        const rank = getCard(cardId).rank;
        counts.set(rank, (counts.get(rank) ?? 0) + 1);
    }
    return counts;
}
function likelyCanBeat(hand, combination, observation) {
    const counts = groupedCount(hand);
    const level = observation.levelRank;
    const maximum = Math.max(...hand.map((cardId) => rankStrength(getCard(cardId).rank, level)), -1);
    const hasFourJokers = (counts.get("SJ") ?? 0) === 2 && (counts.get("BJ") ?? 0) === 2;
    if (hasFourJokers && combination.kind !== "joker-bomb")
        return true;
    for (const [rank, count] of counts) {
        if (count >= 4 && (combination.isBomb || count >= 4)) {
            if (!combination.isBomb || count > combination.bombSize)
                return true;
            if (count === combination.bombSize &&
                isNaturalRank(rank) &&
                rankStrength(rank, level) > combination.mainValue)
                return true;
        }
    }
    if (combination.isBomb)
        return false;
    if (combination.kind === "single")
        return maximum > combination.mainValue;
    if (combination.kind === "pair" || combination.kind === "triple") {
        const needed = combination.kind === "pair" ? 2 : 3;
        return [...counts.entries()].some(([rank, count]) => count >= needed &&
            isNaturalRank(rank) &&
            rankStrength(rank, level) > combination.mainValue);
    }
    return (hand.length >= combination.cardCount && maximum > combination.mainValue + 1);
}
function informationSetScore(play, observation, sample) {
    const opponents = observation.players.filter((player) => player.team !== observation.team && player.finishOrder === null);
    const teammate = observation.players.find((player) => player.seat === teammateOf(observation.seat));
    let score = 0;
    for (const opponent of opponents) {
        score += likelyCanBeat(sample.hands[opponent.seat], play.combination, observation)
            ? -18
            : 12;
    }
    if (teammate && teammate.finishOrder === null) {
        score += likelyCanBeat(sample.hands[teammate.seat], play.combination, observation)
            ? 3
            : -2;
    }
    return score;
}
function activeSeats(hands) {
    return [0, 1, 2, 3].filter((seat) => hands[seat].length > 0);
}
function nextActive(hands, seat) {
    let next = ((seat + 3) % 4);
    for (let count = 0; count < 4; count += 1) {
        if (hands[next].length > 0)
            return next;
        next = ((next + 3) % 4);
    }
    return seat;
}
function nextTrickLeader(hands, winner) {
    const partner = teammateOf(winner);
    return hands[partner].length > 0 ? partner : nextActive(hands, winner);
}
function terminalUtility(hands, rootTeam) {
    const rootRemaining = [0, 1, 2, 3]
        .filter((seat) => teamOf(seat) === rootTeam)
        .reduce((sum, seat) => sum + hands[seat].length, 0);
    const opponentRemaining = [0, 1, 2, 3]
        .filter((seat) => teamOf(seat) !== rootTeam)
        .reduce((sum, seat) => sum + hands[seat].length, 0);
    if (rootRemaining === 0)
        return 500;
    if (opponentRemaining === 0)
        return -500;
    return null;
}
function endgameSearch(position, observation, depth, rootTeam) {
    const terminal = terminalUtility(position.hands, rootTeam);
    if (terminal !== null)
        return terminal;
    if (depth <= 0) {
        return [0, 1, 2, 3].reduce((score, seat) => score +
            (teamOf(seat) === rootTeam
                ? -position.hands[seat].length
                : position.hands[seat].length), 0);
    }
    const hand = position.hands[position.current];
    if (hand.length === 0) {
        return endgameSearch({ ...position, current: nextActive(position.hands, position.current) }, observation, depth - 1, rootTeam);
    }
    const legal = enumerateLegalPlays(hand, position.incumbent, {
        levelRank: observation.levelRank,
        ruleProfile: observation.ruleProfile,
    }, { physicalVariants: false }).slice(0, 5);
    const choices = position.incumbent
        ? [null, ...legal]
        : [...legal];
    const maximizing = teamOf(position.current) === rootTeam;
    let best = maximizing ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
    for (const choice of choices) {
        let nextPosition;
        if (choice === null) {
            const nextPassCount = position.passCount + 1;
            const active = activeSeats(position.hands).length;
            if (nextPassCount >= active - 1 && position.lastSeat !== null) {
                const lead = position.hands[position.lastSeat].length > 0
                    ? position.lastSeat
                    : nextTrickLeader(position.hands, position.lastSeat);
                nextPosition = {
                    ...position,
                    current: lead,
                    incumbent: null,
                    lastSeat: null,
                    passCount: 0,
                };
            }
            else {
                nextPosition = {
                    ...position,
                    current: nextActive(position.hands, position.current),
                    passCount: nextPassCount,
                };
            }
        }
        else {
            const hands = {
                ...position.hands,
                [position.current]: removeCards(hand, choice.cardIds),
            };
            nextPosition = {
                hands,
                current: nextActive(hands, position.current),
                incumbent: choice.combination,
                lastSeat: position.current,
                passCount: 0,
            };
        }
        const value = endgameSearch(nextPosition, observation, depth - 1, rootTeam);
        best = maximizing ? Math.max(best, value) : Math.min(best, value);
    }
    return best;
}
function endgamePlayScore(play, observation, sample) {
    const hands = {
        ...sample.hands,
        [observation.seat]: removeCards(observation.ownHand, play.cardIds),
    };
    const total = observation.players.reduce((sum, player) => sum + player.remainingCards, 0);
    if (total > 16)
        return 0;
    const position = {
        hands,
        current: nextActive(hands, observation.seat),
        incumbent: play.combination,
        lastSeat: observation.seat,
        passCount: 0,
    };
    return endgameSearch(position, observation, 7, observation.team);
}
function choosePlay(observation, legal, options) {
    const teammateHolding = teammateOwnsIncumbent(observation);
    if (teammateHolding && observation.lastPlay !== null) {
        const finishing = legal.find((play) => play.cardIds.length === observation.ownHand.length);
        const mustBlockOpponent = options.difficulty !== "standard" && opponentThreat(observation) <= 2;
        if ((!finishing || options.difficulty === "standard") && !mustBlockOpponent)
            return { play: legal[0], samples: -1 };
    }
    if (options.difficulty === "standard") {
        const nonBombs = legal.filter((play) => !play.combination.isBomb);
        const candidates = nonBombs.length > 0 ? nonBombs : legal;
        const maximumCardCount = Math.max(...candidates.map((play) => play.cardIds.length));
        const sameSize = candidates.filter((play) => play.cardIds.length === maximumCardCount);
        sameSize.sort((left, right) => right.combination.mainValue - left.combination.mainValue ||
            right.cardIds.join().localeCompare(left.cardIds.join()));
        return { play: sameSize[0], samples: 0 };
    }
    const scored = legal.map((play) => ({
        play,
        score: basePlayScore(play, observation, options.difficulty),
    }));
    const styleRandom = createSeededRandom(`${String(options.seed)}:human-style`);
    for (const entry of scored) {
        const variation = options.difficulty === "master" ? 1.5 : 4;
        entry.score += (styleRandom() - 0.5) * variation;
    }
    if (options.difficulty === "master") {
        const deterministicCap = options.maxSamples ??
            Math.max(4, Math.min(48, Math.floor(options.timeBudgetMs * 0.2)));
        let samples = 0;
        for (let index = 0; index < deterministicCap; index += 1) {
            const sample = sampleUnknownHands(observation, `${String(options.seed)}:${index}`);
            for (const entry of scored) {
                entry.score += informationSetScore(entry.play, observation, sample);
                if (index < 4)
                    entry.score += endgamePlayScore(entry.play, observation, sample);
            }
            samples += 1;
        }
        scored.sort((left, right) => right.score - left.score ||
            left.play.cardIds.join().localeCompare(right.play.cardIds.join()));
        return { play: scored[0].play, samples };
    }
    scored.sort((left, right) => right.score - left.score ||
        left.play.cardIds.join().localeCompare(right.play.cardIds.join()));
    return { play: scored[0].play, samples: 0 };
}
export function chooseBotAction(observation, options = {}) {
    const started = Date.now();
    const difficulty = options.difficulty ?? "standard";
    const timeBudgetMs = Math.max(1, options.timeBudgetMs ?? defaultBudgets[difficulty]);
    const seed = options.seed ??
        `${observation.matchId}:${observation.roundNumber}:${observation.version}:${observation.seat}:${difficulty}`;
    if (observation.phase === "tribute") {
        const action = tributeAction(observation);
        if (!action)
            throw new Error("Bot has no pending tribute action");
        return Object.freeze({
            action,
            difficulty,
            searchedSamples: 0,
            elapsedMs: Date.now() - started,
            usedFallback: false,
        });
    }
    if (observation.phase !== "playing" ||
        observation.currentSeat !== observation.seat) {
        throw new Error("Bot may only decide for its own active seat");
    }
    const legal = enumerateLegalPlays(observation.ownHand, observation.lastPlay?.combination ?? null, { levelRank: observation.levelRank, ruleProfile: observation.ruleProfile }, { physicalVariants: false });
    if (observation.lastPlay &&
        (legal.length === 0 ||
            (teammateOwnsIncumbent(observation) &&
                difficulty !== "master" &&
                !(difficulty === "expert" && opponentThreat(observation) <= 2)))) {
        return Object.freeze({
            action: { type: "PASS", seat: observation.seat },
            difficulty,
            searchedSamples: 0,
            elapsedMs: Date.now() - started,
            usedFallback: legal.length === 0,
        });
    }
    if (legal.length === 0) {
        return Object.freeze({
            action: { type: "PASS", seat: observation.seat },
            difficulty,
            searchedSamples: 0,
            elapsedMs: Date.now() - started,
            usedFallback: true,
        });
    }
    const selected = choosePlay(observation, legal, {
        ...options,
        difficulty,
        timeBudgetMs,
        seed,
    });
    if (selected.samples === -1 && observation.lastPlay) {
        return Object.freeze({
            action: { type: "PASS", seat: observation.seat },
            difficulty,
            searchedSamples: 0,
            elapsedMs: Date.now() - started,
            usedFallback: false,
        });
    }
    return Object.freeze({
        action: {
            type: "PLAY",
            seat: observation.seat,
            cardIds: selected.play.cardIds,
        },
        difficulty,
        searchedSamples: selected.samples,
        elapsedMs: Date.now() - started,
        usedFallback: false,
    });
}
export function deterministicRandomChoice(values, seed) {
    if (values.length === 0)
        throw new RangeError("Cannot choose from an empty list");
    const random = createSeededRandom(seed);
    return values[Math.floor(random() * values.length)];
}
export function estimateUnseenRanks(observation) {
    const seen = new Set([...observation.ownHand, ...observation.playedCards]);
    const counts = Object.fromEntries([...NATURAL_RANKS, "SJ", "BJ"].map((rank) => [rank, 0]));
    for (const card of createDeck()) {
        if (!seen.has(card.id))
            counts[card.rank] = (counts[card.rank] ?? 0) + 1;
    }
    return Object.freeze(counts);
}
export function publicPlayerForSeat(observation, seat) {
    const player = observation.players.find((candidate) => candidate.seat === seat);
    if (!player)
        throw new RangeError(`Missing public player ${seat}`);
    return player;
}
//# sourceMappingURL=index.js.map